import { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';

import Url from 'types/Url';
import OptionGroup from 'types/ProductOptionGroup';
import { VariantAttributeValue } from 'types/models/VariantAttributeValue';
import { getVariantSelectorRoute } from 'apiRoutes/variants';
import { ProductContext } from '@providers/ProductProvider';
import { ConfigContext } from '@providers/ConfigProvider';
import Options from '@templates/product/Options';
import Switch from '@components/atoms/Switch';
import Typography from '@components/atoms/Typography';
import { FormattedMessage } from 'utils/intl';
import { getSelectVariantPayload } from 'utils/analytics/payload/selectVariant';
import useBoolean from 'hooks/useBoolean';
import useTrack from 'hooks/useTrack';
import useFetch from 'hooks/useFetch';

import { getVariantsWithAttributeValue, getFilteredOptionGroups } from './VariantSelector.utils';
import classes from './VariantSelector.module.scss';
import messages from './VariantsSelector.messages';

const variantSelectorFilteringAlgorithms = ['with-filtering', 'with-promotion-and-filtering'];

const VariantsSelector = (): JSX.Element => {
  const router = useRouter();
  const { variantSelectorAlgorithm } = useContext(ConfigContext);
  const { id, optionGroups, selectedOptions } = useContext(ProductContext);
  const { trackLink } = useTrack();
  const [getPredictions, { data: predictionsData }] = useFetch(getVariantSelectorRoute); // check;

  const [onlyAvailableVariantsInSelector, { setValue: setOnlyAvailableVariantsInSelector }] = useBoolean(false);
  const [availableOptionGroups, setAvailableOptionGroups] = useState<OptionGroup[]>([]);
  const [predictedOptionGroups, setPredictedOptionGroups] = useState<OptionGroup[]>([]);

  const hasVariantsFiltering = variantSelectorFilteringAlgorithms.includes(variantSelectorAlgorithm);

  const predictions = useMemo(() => predictionsData?.predictions || {}, [predictionsData]);
  const availableShopsVariants = useMemo(() => predictionsData?.availableShopsVariants || [], [predictionsData]);

  useEffect(() => {
    getPredictions({ id, withPredictions: true });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectOption = useCallback(
    (variantId: number, href: Url) => {
      trackLink(
        'common',
        'select_variant',
        getSelectVariantPayload(id, variantId)
      )(() => {
        router.push(href);
        setPredictedOptionGroups([]);
        setAvailableOptionGroups([]);
      });
    },
    [id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onHoverOption = useCallback(
    (groupId: string, variantId?: number, variantAttributeValue?: VariantAttributeValue, hoverIn?: boolean) => {
      const groups = variantId && hoverIn ? predictions[groupId]?.[variantId] || [] : [];
      const variantsWithAttributeValue = getVariantsWithAttributeValue(availableShopsVariants, variantAttributeValue);
      const newPredictedOptionGroups = getFilteredOptionGroups(groups, (option) => option.variantId === variantId);
      const newAvailableOptionGroups = getFilteredOptionGroups(
        groups,
        (option) => !!option.variantId && variantsWithAttributeValue.includes(option.variantId)
      );

      setPredictedOptionGroups(newPredictedOptionGroups);
      setAvailableOptionGroups(newAvailableOptionGroups);
    },
    [predictions, availableShopsVariants]
  );

  return !!optionGroups.length || onlyAvailableVariantsInSelector ? (
    <div>
      {hasVariantsFiltering && (
        <Switch
          id="onlyAvailableVariantsInSelector"
          classes={{ root: classes.onlyAvailableCheckbox }}
          checked={onlyAvailableVariantsInSelector}
          value={onlyAvailableVariantsInSelector}
          onChange={({ target: { checked } }) => setOnlyAvailableVariantsInSelector(checked)}
        >
          <Typography variant="body2">
            <FormattedMessage {...messages.onlyAvailableVariants} />
          </Typography>
        </Switch>
      )}
      <Options
        selectedOptions={selectedOptions}
        optionGroups={optionGroups}
        availableOptionGroups={availableOptionGroups}
        predictedOptionGroups={predictedOptionGroups}
        onHoverOption={onHoverOption}
        onSelectOption={onSelectOption}
      />
    </div>
  ) : (
    <></>
  );
};

export default VariantsSelector;
